// src/services/hafizRulesService.js
//
// Tenant-level "what counts as memorised" rules.
//
// The auto-promotion detector in memorizationSuggestionsService ships
// with sensible defaults. The moudir, however, may want to tighten or
// loosen those thresholds for their own madrasa — e.g. demand 5 passes
// instead of 3, or accept tajweed ≥ 60 instead of 70 for younger
// classes. Those choices live here.
//
// Storage: `tenants/{tenantId}/settings/hafizRules` — a single doc per
// tenant. Reads are cached for the duration of a tab session because
// the detector is called inline on every wird save and we don't want
// to chase the same doc dozens of times.

import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { DEFAULT_THRESHOLDS } from './memorizationSuggestionsService';

const SETTINGS_PATH = (tenantId) => ['tenants', tenantId, 'settings', 'hafizRules'];

const FIELD_KEYS = ['minPasses', 'minDistinctDates', 'minHifz', 'minTajweed'];

const cache = new Map(); // tenantId -> rules
let cachePromise = new Map(); // tenantId -> Promise<rules> (in-flight reads)

/**
 * Coerce a raw doc (or partial input from the editor) into the canonical
 * rules shape. Falls back to DEFAULT_THRESHOLDS for any missing or
 * out-of-range field so the detector is always called with valid numbers.
 */
export function normalizeRules(input) {
  const out = { ...DEFAULT_THRESHOLDS };
  if (!input || typeof input !== 'object') return out;
  for (const k of FIELD_KEYS) {
    const v = Number(input[k]);
    if (Number.isFinite(v) && v >= 0) out[k] = Math.round(v);
  }
  // Hifz/Tajweed are 0..100 caps; passes/dates must be ≥ 1.
  out.minHifz = Math.min(100, Math.max(0, out.minHifz));
  out.minTajweed = Math.min(100, Math.max(0, out.minTajweed));
  out.minPasses = Math.max(1, out.minPasses);
  out.minDistinctDates = Math.max(1, out.minDistinctDates);
  // The kill-switch is optional — when explicitly false, the detector
  // is no-op'd by callers (see wirdRatingsService).
  out.enabled = input.enabled === false ? false : true;
  return out;
}

/**
 * Load the rules for a tenant. Returns DEFAULT_THRESHOLDS if no doc
 * exists. Cached per tenantId.
 */
export async function getHafizRules(tenantId) {
  if (!tenantId) return { ...DEFAULT_THRESHOLDS, enabled: true };
  if (cache.has(tenantId)) return cache.get(tenantId);
  if (cachePromise.has(tenantId)) return cachePromise.get(tenantId);

  const p = (async () => {
    try {
      const snap = await getDoc(doc(db, ...SETTINGS_PATH(tenantId)));
      const rules = normalizeRules(snap.exists() ? snap.data() : null);
      cache.set(tenantId, rules);
      return rules;
    } catch (e) {
      // Fall back to defaults but don't poison the cache so a transient
      // permission glitch doesn't lock the user into defaults forever.
      return normalizeRules(null);
    } finally {
      cachePromise.delete(tenantId);
    }
  })();
  cachePromise.set(tenantId, p);
  return p;
}

/**
 * Persist the rules. Invalidates the cache so subsequent reads see the
 * new values immediately.
 */
export async function setHafizRules(tenantId, rules) {
  if (!tenantId) throw new Error('setHafizRules: tenantId is required');
  const clean = normalizeRules(rules);
  await setDoc(
    doc(db, ...SETTINGS_PATH(tenantId)),
    {
      ...clean,
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );
  cache.set(tenantId, clean);
  return clean;
}

/** Clear the cache — useful in tests and when the tenantId switches. */
export function __clearHafizRulesCache() {
  cache.clear();
  cachePromise.clear();
}

export const __test__ = { normalizeRules };
